import { parseNumber } from "../converters/numberInput";

/** Largest amount the budget and loan tools accept. Anything bigger is almost certainly a typo. */
export const MAX_AMOUNT = 1e9;

/** Typed money text → number. Strips currency symbols and %, then reads thousands separators like parseNumber. Null if unreadable ("abc", "1e3", ""). */
export function parseMoney(raw: string): number | null {
  const s = String(raw ?? "").replace(/[$€£¥₹%]/g, "");
  return parseNumber(s);
}

export type Check = { value: number; error?: undefined } | { value: null; error: string };

export interface AmountOpts {
  /** Blank counts as invalid unless a default is given. */
  blank?: number;
  /** Allow negative numbers (default false). */
  negative?: boolean;
  min?: number;
  max?: number;
  /** Reads "<label> ..." in error text. */
  label?: string;
  /** Whole numbers only. */
  integer?: boolean;
}

const grouped = (n: number) => n.toLocaleString("en-US");

/** Validates a typed amount and gives the exact message to show beside the box. */
export function checkAmount(raw: string, o: AmountOpts = {}): Check {
  const label = o.label ?? "Amount";
  const max = o.max ?? MAX_AMOUNT;
  if (!String(raw ?? "").trim()) {
    return o.blank === undefined ? { value: null, error: `${label}: enter a number (0 is fine).` } : { value: o.blank };
  }
  const n = parseMoney(raw);
  if (n === null) return { value: null, error: `${label}: "${String(raw).trim().slice(0, 20)}" isn't a number. Use digits like 1,250.50.` };
  if (o.integer && !Number.isInteger(n)) return { value: null, error: `${label}: whole numbers only (no decimals like ${n}).` };
  if (!o.negative && n < 0) return { value: null, error: `${label}: can't be negative.` };
  if (o.min !== undefined && n < o.min) return { value: null, error: `${label}: must be at least ${grouped(o.min)}.` };
  if (n > max) return { value: null, error: `${label}: capped at ${grouped(max)}.` };
  return { value: n };
}

/** Dollar text with the minus sign in front of the dollar sign ("-$450"), rounded to cents. */
export function formatUsd(n: number, minDigits = 0): string {
  if (!Number.isFinite(n)) return "—";
  const cents = Math.round(Math.abs(n) * 100);
  const body = (cents / 100).toLocaleString("en-US", { minimumFractionDigits: minDigits, maximumFractionDigits: 2 });
  return (n < 0 && cents > 0 ? "-$" : "$") + body;
}

/** Round to whole cents without float drift (1.005 stays 1.01). */
export const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
